import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  BaseComponent,
  FormatService,
  FormlyService,
  FormOkCancelModalComponent,
  FormService,
  ModalService,
} from '../../../tms-shared';
import { AuthService } from '../../../auth';
import { Comment, CreateComment } from '../../models';
import { BaseCommentService, MockCommentService } from '../../services';

@Component({
  selector: 'app-base-comments',
  templateUrl: './base-comments.component.html',
  styleUrl: './base-comments.component.scss',
})
export class BaseCommentsComponent extends BaseComponent implements OnInit {
  @Input() commentService: BaseCommentService = new MockCommentService();
  @Input() id = '';
  @Input() commentStart = '';
  @Input() oneRecord = false;
  @Input() allowAdd = true;

  comments$: Observable<Comment[]>;
  fields: FormlyFieldConfig[];
  comment: string;

  constructor(
    private authService: AuthService,
    private formService: FormService,
    private formlyService: FormlyService,
    private formatService: FormatService,
    private modalService: ModalService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.id === '') {
      this.id = this.route.snapshot.paramMap.get('id');
    }
    this.getFields();
    this.getModel();
  }

  isCurrentUser(comment: Comment) {
    return comment.userId == this.authService.userId;
  }

  canAmendOtherComments() {
    return this.authService.getCanAmendComments();
}

  multiLineComment(comment: string) {
    return this.formatService.multiLineToHtml(comment);
  }

  saveComments() {
    if (!this.comment) {
      return;
    }

    this.commentService
      .save({
        id: this.id,
        comment: this.comment,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.formService.markAsSaved();
        this.getModel();
      });
  }

  edit(comment: Comment) {
    const { modal, component } = this.modalService.openMedium<FormOkCancelModalComponent>(FormOkCancelModalComponent);
    component.header = $localize`Update Comment`;
    component.okLabel = $localize`Update`;
    component.fields = this.fields;
    component.model = {
      id: comment.id,
      comment: comment.comment,
    } as CreateComment;
    modal.result.then(
      (model: Comment) =>
        this.commentService.update(model).subscribe(() => {
          this.formService.markAsSaved();
          this.getModel();
        }),
      () => {},
    );
  }

  delete(id: string) {
    this.commentService
      .delete(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.formService.markAsSaved();
        this.getModel();
      });
  }

  private getFields() {
    this.fields = [this.formlyService.textarea('comment', $localize`Comment`)];
  }

  private getModel() {
    this.comment = this.commentStart;
    this.comments$ = this.oneRecord ? this.commentService.getOneRecord(this.id) : this.commentService.get(this.id);
  }
}
